import * as React from "react";
import { Check, ChevronsUpDown, Star, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { normalizeString } from "@/utils/searchUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface AgenciaMultiSelectOption {
  id: string;
  nome: string;
  favorita?: boolean;
}

interface AgenciaMultiSelectProps {
  options: AgenciaMultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Seletor de VÁRIAS agências com busca/autocompletar. Favoritas aparecem no
 * topo (com estrelinha), depois o resto em ordem alfabética. Usado no diálogo
 * de match por agência (permite marcar várias de uma vez).
 */
export function AgenciaMultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Selecione as agências...",
  disabled = false,
  className,
}: AgenciaMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  // Favoritas primeiro, depois ordem alfabética dentro de cada grupo.
  const ordenadas = React.useMemo(() => {
    return [...options].sort((a, b) => {
      if (!!a.favorita !== !!b.favorita) return a.favorita ? -1 : 1;
      return a.nome.localeCompare(b.nome);
    });
  }, [options]);

  const filtradas = React.useMemo(() => {
    const buscaNorm = normalizeString(search);
    if (!buscaNorm) return ordenadas;
    return ordenadas.filter((o) =>
      normalizeString(o.nome).includes(buscaNorm),
    );
  }, [ordenadas, search]);

  const nomeById = React.useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((o) => map.set(o.id, o.nome));
    return map;
  }, [options]);

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const remove = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(selected.filter((s) => s !== id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between min-h-10 h-auto",
            selected.length === 0 && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1 flex-1 text-left">
            {selected.length === 0 ? (
              <span>{placeholder}</span>
            ) : (
              selected.map((id) => (
                <Badge
                  key={id}
                  variant="secondary"
                  className="mr-1 mb-1 text-xs"
                >
                  {nomeById.get(id) ?? "Agência"}
                  <span
                    role="button"
                    tabIndex={0}
                    className="ml-1 rounded-full outline-hidden ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") remove(id, e as never);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => remove(id, e)}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full p-0"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar agência..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>Nenhuma agência encontrada.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {filtradas.map((option) => {
                const isSelected = selected.includes(option.id);
                return (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => toggle(option.id)}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="flex-1 truncate">{option.nome}</span>
                    {option.favorita && (
                      <Star className="ml-2 h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
